<template>
  <v-container>
    <div class="tableRow">
      <div>
        <v-btn @click="undo" :disabled="!history.canUndo" class="undo">Undo</v-btn>
        <v-btn @click="redo" :disabled="!history.canRedo" class="redo">Redo</v-btn>
      </div>
      <div>
        <v-btn @click="toggleComparison" class="toggleComparison">Compare to</v-btn>
      </div>
      <div>
        <v-btn @click="exportJson" class="save">Save</v-btn>
        <v-btn @click="showImport=true;" class="load">Load</v-btn>
      </div>
    </div>
    <v-switch v-model="drawBookmark" label="Show Bookmark" class="showBookmark"></v-switch>
    <svg ref="view" :width="display.side.width-20" :height="display.main.innerHeight-200">
      <g class="tree" :transform="`translate(${padding[1]+15}, ${padding[0]})`"></g>
    </svg> 
    <file-modal ref="importModal" accept="application/json" :showModal="showImport" @close="cancelImport" @done="importJson" inputMsg="Import history file (json)">
    </file-modal>
  </v-container>
</template>

<script>

import * as d3 from "d3";
import { saveAs } from 'file-saver';
import FileModal from "@/modals/FileModal";
import { History } from "@/History";
import { ComparisonMode } from "@/Config.js";
import { mapGetters, mapMutations, mapActions } from "vuex";

export default {
  components: {
    FileModal,
  },
  data(){
    return {
      padding: [70, 0, 20, 20],
      showImport: false,
      comparisonToggled: false,
      drawBookmark: false,
      zoom: d3.zoom(),
    }
  },
  watch: {
    history: {
      handler(history) {
        this.draw(history);
      },
      deep: true
    },
    display() {
      this.draw(this.history);
    },
    comparison_mode() {
      this.draw(this.history);
    },
    drawBookmark() {
      this.draw(this.history);
    },
  },
  computed: {
    ...mapGetters(['history', 'display', 'comparison_mode', 'comparison_nodes'])
  },
  mounted() {
    this.zoom.scaleExtent([1,1]).on('zoom', this.handleZoom);
    d3.select(this.$refs.view).call(this.zoom);
    this.draw(this.history);
  },
  methods: {
    ...mapMutations(['set_history']),
    ...mapActions(['start_new_comparison']),
    handleZoom(e) {
      d3.select(this.$refs.view).select('.tree').attr('transform', e.transform);
    },
    draw(history) {
      //In general, we decide to not touch the original data (history.tree) but to set the visibility of certain nodes based on
      //the expansion of parents and the benchmark feature.

      let tree = history.tree;
      let svg = d3.select(this.$refs.view);
      const t = svg.transition()
                   .duration(750);
      const barHeight = 25;

      var root = d3.hierarchy(tree);
      var nodes = root.descendants();

      //Is this node visible? We consider parents' extension and bookmark features
      let isVisible = (d) => {
        if(d == null) return true;
        else if(d == root) return true;

        //Define for parents
        for(let node = d.parent; node != null; node = node.parent)
          if(!node.data.extend)
            return false;

        //Define for bookmark
        if(this.drawBookmark)
        {
            if(d.data.bookmarked)
              return true;

            //Draw all the nodes between the deepest bookmarked node and the current node
            for(let node = history.curNode; node != null; node = node.parent)
            {
                if(node == d.data)
                    return true;
                else if(node.bookmarked)
                    return false;
            }

          return false;
        }
        return true;
      };

      //Set the visibility of all nodes.
      let setVisibility_rec = (n) => {
        if(n == null)
          return;
        n.isVisible = isVisible(n);
        if(n.children != null)
          n.children.forEach((c) => setVisibility_rec(c));
      };
      setVisibility_rec(root);

      var index = -1;


      //Should we consider to add children of node (n) at a deeper depth?
      //Note that this algorithm considers the tree as is in its visible state
      //Grandchildren can thus appear to be immediate children is direct children are not visible
      let addDepthForChildren = (n) => {
        let nbChildrenVisible = (c) => {
          if(c.isVisible)
            return 1;

          let res = 0;
          if(c.children != null)
            for(let child of c.children)
              res += nbChildrenVisible(child);
          return res;
        };

        let nbVisible = 0;
        for(let child of n.children)
          nbVisible += nbChildrenVisible(child);
        return nbVisible > 1;
      };

      let computeDepthChildrenGT2 = (n) => {
        let depth=0;
        for(let curNode = n.parent; curNode != null; curNode = curNode.parent)
          if(curNode.isVisible && addDepthForChildren(curNode)) 
            depth++;
        return depth;
      };

      //Set the position of all nodes depending on their position in the hierarchy, and whether the parent is expanded or not
      let setPosition_rec = (n) => {
        n.y = index * barHeight;
        n.x = computeDepthChildrenGT2(n) * 15 + 20;

        if(!n.data.ephemeral && n.isVisible)
          index++;

        if(n.children != null)
          n.children.forEach((c) => setPosition_rec(c));
      };
      setPosition_rec(root);

      //Set the class of the node on whether it is selected or not
      let getClass = (d) =>
      {
        if(d.data === history.curNode)
          return "node selected";
        return "node" + (this.comparisonToggled || (this.comparison_mode != ComparisonMode.NONE_COMPARISON_MODE && this.comparison_nodes.includes(d.data)) ? ' comparison' : '');
      };

      //Function called when the label should be renamed
      let onClickRenameLabel = function(d) {
        var result = prompt('Change the name of the node', d.data.label);
        if(result != null)
         d.data.label = result; 
      };

      //Redo the labels as they should be
      let redoLabelTspan = function(text) {
         text.selectAll("*").remove();
         text.attr("x", 15)
             .attr("dy", 4)
             .text((d) => d.data.label)
             .on("click", (event, d) => {
               onClickRenameLabel(d);
               event.stopPropagation();
             });
      };

      let expandOffset = -18;
      //Function to display the arrow to expand parents
      let redoArrow = function(d) {
        if(d.data.children == null || d.data.children.length < 1) return "";
        if(d.data.extend)
          return d3.line()([[-10+expandOffset, -5], [10+expandOffset, -5], [0+expandOffset, 5]]);
        return d3.line()([[-5+expandOffset, -10], [-5+expandOffset, 10], [5+expandOffset, 0]]);
      };

      //Draw the line between parents to children when we change depth values
      let drawLineParentChild = (d) => {
        for(let curNode = d.source; curNode != null; curNode = curNode.parent) {
          if(curNode.isVisible) {
            if(addDepthForChildren(curNode))
              return d3.line()([[curNode.x+expandOffset, curNode.y+5], [curNode.x+expandOffset, d.target.y], [d.target.x-5+expandOffset, d.target.y]]);
            return "";
          }
            
        }
        return "";
      }

      //What to do when a node is clicked?
      let onClickNode = (d) => {
        if(!this.comparisonToggled){
          if(this.comparison_mode == ComparisonMode.NONE_COMPARISON_MODE)
            this.history.jumpTo(d);
          else if(this.comparison_nodes[this.comparison_mode] == d) 
            this.history.jumpTo(this.history.tree); //Hard reset
            this.history.jumpTo(d);
        }
        else if(d.data != this.history.curNode) {
          this.start_new_comparison(d);
          this.toggleComparison();
        }
        else
          this.toggleComparison();
        event.stopPropagation();
      };

      // Display the nodes. For each node: Render an arrow to expand it (or not), render a circle, then render the label
      svg.select('.tree').selectAll("g.node")
         .data(nodes)
         .join(
           //Create the following structure: 
           //<g>
           //  <g class='nodeG'>
           //    <text class='label'>
           //    </text>
           //    <circle class='node'>
           //    </circle>
           //  </g>
           //  <path class='arrow'>
           //  </path>
           //  <path class='bookmark'>
           //  </path>
           //</g>
           enter => {
             let g = enter.append('g');
             let nodeG = g.attr("class", (d) => getClass(d))
                          .attr("transform", (d) => "translate(" + d.x + "," + d.y + ")")
                          .attr("style", (d) => "display: "+(!d.data.ephemeral && isVisible(d) ? "block" : "none"))
                          .on("contextmenu", (event, d) => {
                            event.preventDefault();
                            onClickRenameLabel(d);
                          })
                          .append("g")
                            .attr("class", "nodeG")
                            .attr("transform", "translate(20, 0)")
          
             let text = nodeG.append("text").attr("class", "label");
             redoLabelTspan(text);
    
             nodeG.append("circle")
                  .attr("r", 10)
                  .on("click", (event, d) => {
                    onClickNode(d.data);
                  });

             g.append('path')
              .attr('fill', 'grey')
              .attr('class', 'arrow')
              .attr('font-size', '12px')
              .attr("d", (d) => redoArrow(d))
              .on("click", (event, d) => {d.data.extend = !d.data.extend;});

             g.append('path')
              .attr('fill', (d) => (d.data.bookmarked ? 'yellow' : 'white'))
              .attr('class', 'bookmark')
              .attr('d', d3.line()([[-6, 8], [0, 5], [6, 8], [6, -8], [-6, -8], [-6, 8]]))
              .on("click", (event, d) => {d.data.bookmarked = !d.data.bookmarked})
           },

           update => {
             update.attr("class", (d) => getClass(d))
               .call(update => update.transition(t))
               .attr("transform", (d) => "translate(" + d.x + "," + d.y + ")")
               .attr("style", (d) => "display: "+(!d.data.ephemeral && isVisible(d) ? "block" : "none"))
               .select(".nodeG")
                  .select("circle")
                    .attr("r", 10)
                    .on("click", (event, d) => onClickNode(d.data));

               let text = update.select(".nodeG").select(".label");
               redoLabelTspan(text);

               update.select(".arrow")
                     .attr("d", (d) => redoArrow(d))


               update.select(".bookmark")
                .attr('fill', (d) => (d.data.bookmarked ? 'yellow' : 'white'));
             },

           exit => exit.call(exit => exit.remove())
         );

      //Handle the link between parent to child
      svg.select(".tree").selectAll("path.link")
         .data(root.links())
         .join(
           enter => enter.append("path")
             .attr("class", "link")
             .attr("d", (d) => drawLineParentChild(d))
             .attr("style", (d) => "display: "+(isVisible(d.target) ? "block": "none")),

           update => update.call(update => update.transition(t))
             .attr("d", (d) => drawLineParentChild(d))
             .attr("style", (d) => "display: "+(isVisible(d.target) ? "block": "none")),

           exit => exit.call(exit => exit.remove()));

      root.each((d) => {
        d.x0 = d.x;
        d.y0 = d.y;
      });

      //Handle panning
      let bbox       = this.$refs.view.getBBox();
      let clientRect = this.$refs.view.getBoundingClientRect();
      let topLeft = [-(this.padding[1]+15), -this.padding[0]];
      let extent  = [topLeft, [bbox.width, bbox.height]];
      this.zoom.extent([[0,0], [Math.min(bbox.width-topLeft[0], clientRect.width), Math.min(bbox.height-topLeft[1], clientRect.height)]])
               .translateExtent(extent);
    },

    undo() {
      this.history.undo();
    },

    redo() {
      this.history.redo();
    },

    importJson(file) {
      let reader = new FileReader();
      reader.onload = (event) => { 
        this.set_history(History.importFromJSON(event.target.result));
        this.showImport = false;
      }
      reader.readAsText(file);
    },

    exportJson() {
      var blob = new Blob([this.history.toString()], { 
        type: "application/json;charset=utf-8"
      });
      saveAs(blob, "history.json");
    },

    cancelImport() {
      this.showImport = false;
    },

    toggleComparison() {
      this.comparisonToggled = !this.comparisonToggled;
      this.draw(this.history);
    }
  }
};
</script>
<style>
  .node circle {
    fill: gray;
  }

  .node.selected circle {
    stroke: steelblue;
    fill: black;
    stroke-width: 4px;
  }
  .node.comparison circle {
    stroke: green;
    stroke-width: 4px;
  }

  .node text {
    text-anchor: start;
    font-size: smaller;
  }

  .link {
    fill: none;
    stroke: black;
    stroke-width: 2px;
  }

  .tableRow {
    display: table;
    table-layout: fixed;
    align-content: center;
    align-items: center;
    text-align: center;
    width: 100%;
  }

  .tableRow > div {
    display: table-cell;
    width: auto;
  }

  .tableRow div:last-child {
    position: absolute;
    right: 0;
  }

  .tableRow div:first-child {
    position: absolute;
    left: 0;
  }

  .bookmark {
    stroke-width: 2px;
    stroke: black;
  }
</style>
